import * as React from 'react';
import { useState } from 'react';

import { useAtom } from 'jotai';

import { translate } from '~l10n/client';
import { AlertMessage } from '~shared/components/AlertMessage';
import { IconButton, SmallButton } from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/large';
import Textarea from '~shared/components/Textarea/Textarea';
import {
  Error as ErrorIcon,
  Pending,
} from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import { AiAssistedBadgeLink } from '~teacher/components/QuizEditor/AiAssistedBadgeLink';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import { handleAiRateLimitError } from '~teacher/components/QuizEditor/errorHandlers/aiRateLimitErrorHandler';
import { useImportFromText } from '~teacher/hook/ai';

import { positionAtom } from './ImportFromTextDialog';

const MAX_IMPORT_QUESTION_LENGTH = 3000;

enum ImportFromTextValidation {
  Empty = 'empty',
  Valid = 'valid',
  Invalid = 'invalid',
}

interface Props {
  onSuccess: () => void;
}

export function ImportFromText({ onSuccess }: Props) {
  const [textToImport, setTextToImport] = useState<string>('');
  const { status, reset, handleImportFromText } = useImportFromTextDialog({
    onSuccess,
  });

  switch (status) {
    case 'loading':
    case 'success':
      return <Loading goBack={() => reset()} />;
    case 'error':
      return (
        <Error
          onRetry={handleImportFromText}
          textToImport={textToImport}
          goBack={() => reset()}
        />
      );
    case 'idle':
      return (
        <ImportFromTextForm
          onImportFromText={handleImportFromText}
          textToImport={textToImport}
          setTextToImport={setTextToImport}
        />
      );
  }
}

function ImportFromTextForm({
  onImportFromText,
  textToImport,
  setTextToImport,
}: {
  onImportFromText: (textToImport: string) => void;
  textToImport: string;
  setTextToImport: (textToImport: string) => void;
}) {
  const [importFromTextValidation, setImportFromTextValidation] =
    useState<ImportFromTextValidation>(ImportFromTextValidation.Empty);

  return (
    <>
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <AiAssistedBadgeLink
                onClick={() => {
                  /* TODO ADD ANALYTICS
                new FeatureUseEventAdapter({
                  feature: 'ai_assisted_link',
                  source: 'generate_questions_step_1',
                  action: 'click',
                }).sendAnalytics();
                 */
                }}
              />
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700">
              {translate('Copy-Paste Questions')}
            </div>
            <div className="text-sans text-base leading-normal text-blue-600">
              {translate(
                'Add questions by pasting them from another quiz resource.'
              )}
            </div>
          </div>
        </div>
      </div>
      <form
        className="h-full"
        onSubmit={(event) => {
          event.preventDefault();
          if (!textToImport.trim()) {
            setImportFromTextValidation(ImportFromTextValidation.Invalid);
          } else {
            onImportFromText(textToImport);
          }
        }}
      >
        <div className="flex flex-col h-full justify-between gap-5">
          <div
            className="flex justify-between flex-col pt-4 gap-2 h-full"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <div className="flex flex-col gap-2 flex-grow">
              <div className="flex flex-row justify-between items-end">
                <label
                  className="label text-base text-sans text-blue-900 leading-8"
                  htmlFor="text-to-import"
                >
                  {translate('Questions')}
                </label>
                <div>
                  <span className="text-blue-600 leading-6 font-sans text-xs">
                    {textToImport.length}/{MAX_IMPORT_QUESTION_LENGTH}
                  </span>
                </div>
              </div>
              <div className="flex flex-grow flex-col gap-2">
                <div className="flex-grow">
                  <Textarea
                    value={textToImport}
                    maxLength={MAX_IMPORT_QUESTION_LENGTH}
                    autosize={false}
                    noResize
                    id="text-to-import"
                    className="w-full h-full resize-none"
                    placeholder={translate(
                      'Paste in multiple-choice, true / false, and short answer questions.'
                    )}
                    onChange={(event) => {
                      if (
                        !!event.target.value.trim() &&
                        importFromTextValidation ===
                          ImportFromTextValidation.Invalid
                      ) {
                        setImportFromTextValidation(
                          ImportFromTextValidation.Empty
                        );
                      }
                      setTextToImport(event.target.value);
                    }}
                  />
                </div>
                <div className="flex-shrink">
                  <p className="text-blue-600 leading-6 font-sans text-xs">
                    {translate(
                      'Limited to multiple-choice, true / false, and short answer questions.'
                    )}
                  </p>
                  <p className="text-blue-600 leading-6 font-sans text-xs">
                    {translate(
                      'Generated questions can make mistakes. Consider checking question accuracy.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div role="region" aria-live="polite">
            {importFromTextValidation === ImportFromTextValidation.Invalid ? (
              <AlertMessage tone="critical" showIcon>
                {translate('Required field(s) missing')}
              </AlertMessage>
            ) : null}
          </div>
          <div
            className="flex justify-end pt-8"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <SmallButton type="submit" appearance="primary">
              {translate('Add')}
            </SmallButton>
          </div>
        </div>
      </form>
    </>
  );
}

function HeaderWithBackButton({ goBack }: { goBack: () => void }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col">
        <IconButton aria-label={translate('Back')} onClick={goBack}>
          <IconChevron direction="left" />
        </IconButton>
      </div>
      <div className="flex flex-shrink flex-col">
        <div className="flex">
          <AiAssistedBadgeLink
            onClick={() => {
              /* TODO ADD ANALYTICS
            new FeatureUseEventAdapter({
              feature: 'ai_assisted_link',
              source: 'generate_questions_step_1',
              action: 'click',
            }).sendAnalytics();
             */
            }}
          />
        </div>
        <div className="font-display text-md leading-normal text-blue-900 font-700">
          {translate('Copy-Paste Questions')}
        </div>
        <div className="text-sans text-base leading-normal text-blue-600">
          {translate(
            'Add questions by pasting them from another quiz resource.'
          )}
        </div>
      </div>
    </div>
  );
}

function Loading({ goBack }: { goBack: () => void }) {
  return (
    <>
      <div className="pb-4">
        <HeaderWithBackButton goBack={goBack} />
      </div>
      <div
        className="grid items-center justify-center h-full"
        style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
      >
        <div className="flex flex-col items-center gap-8">
          <Pending />
          <div className="flex flex-col items-center">
            <p className="text-display font-display text-blue-900 font-500 text-md leading-10">
              {translate('Importing')}
            </p>
            <p className="font-sans text-blue-900 text-sm leading-8  ">
              {translate(
                'Please keep this window open while we import your data.'
              )}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Error({
  textToImport,
  onRetry,
  goBack,
}: {
  textToImport: string;
  onRetry: (importFromText: string) => void;
  goBack: () => void;
}) {
  return (
    <>
      <div className="pb-4">
        <HeaderWithBackButton goBack={goBack} />
      </div>
      <div
        className="grid items-center justify-center h-full"
        style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
      >
        <div className="flex flex-col items-center gap-3">
          <ErrorIcon />
          <span className="pt-4 font-display text-md font-500 text-blue-900">
            {translate('Something Went Wrong')}
          </span>
          <span className="max-w-2xl pt-3 font-sans text-base leading-8 text-blue-900 text-center">
            {translate('An unknown error occurred. Please try again later.')}
          </span>
          <SmallButton
            className="my-1"
            onClick={() => onRetry(textToImport)}
            appearance="primary"
          >
            {translate('Try Again')}
          </SmallButton>
        </div>
      </div>
    </>
  );
}

function useImportFromTextDialog({ onSuccess }: { onSuccess: () => void }) {
  const { quizId, workspaceQuizId, workspaceId } = useQuizContext();
  const [position] = useAtom(positionAtom);

  const importFromText = useImportFromText({
    quizId,
    workspaceId,
    workspaceQuizId,
    insertionOrder: position,
  });

  const handleImportFromText = (textQuestions: string) => {
    importFromText.mutate(
      { textQuestions: textQuestions },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (error: unknown) => {
          handleAiRateLimitError(error);
        },
      }
    );
  };

  return { ...importFromText, handleImportFromText: handleImportFromText };
}
